/**
 * Types for the player offline caching system.
 */

/** Size threshold: assets ≥ this size go to OPFS, others to Cache Storage */
export const OPFS_SIZE_THRESHOLD = 50 * 1024 * 1024; // 50 MB

/**
 * OPFS filename for an asset. Shared by the OPFS worker (writes/streaming
 * downloads) and the main thread (disk-backed playback reads).
 */
export function opfsAssetFileName(assetId: string): string {
  return `asset-${assetId}.bin`;
}

/** Cache version for Service Worker cache naming */
export const CACHE_VERSION = 'v1';

/** Cache name for static app assets (JS, CSS, HTML) */
export const STATIC_CACHE_NAME = `dooh-static-${CACHE_VERSION}`;

/** Cache name for content assets (images, small videos) */
export const CONTENT_CACHE_NAME = `dooh-content-${CACHE_VERSION}`;

/** IndexedDB database name */
export const IDB_NAME = 'dooh-player';

/** IndexedDB database version */
export const IDB_VERSION = 1;

/** IndexedDB store names */
export const IDB_STORES = {
  manifests: 'manifests',
  assetMeta: 'assetMeta',
} as const;

/** Manifest TTL in milliseconds (24 hours) */
export const MANIFEST_TTL_MS = 24 * 60 * 60 * 1000;

/** Disk headroom percentage for eviction policy */
export const DISK_HEADROOM_PERCENT = 20;

/** Metadata for a cached asset */
export interface CachedAssetMeta {
  /** Asset ID */
  assetId: string;
  /** Original URL */
  url: string;
  /** SHA-256 checksum from server */
  expectedChecksum: string;
  /** Whether checksum was verified */
  checksumVerified: boolean;
  /** File size in bytes */
  fileSize: number;
  /** Asset type */
  assetType: 'image' | 'video';
  /** Native media duration in seconds (videos; null for images/unknown) */
  duration?: number | null;
  /** Storage backend used */
  storageBackend: 'cache-storage' | 'opfs';
  /** Campaign IDs that reference this asset */
  campaignIds: string[];
  /** Last played timestamp (for LRU) */
  lastPlayedAt: number | null;
  /** When the campaign(s) for this asset end (for expired eviction) */
  campaignEndDates: string[];
  /** Whether this asset is in the fallback playlist */
  isFallback: boolean;
  /** When this was cached */
  cachedAt: number;
}

/** Manifest record stored in IndexedDB */
export interface ManifestRecord {
  /** Version hash from the manifest */
  version: string;
  /** The full manifest JSON */
  manifestJson: string;
  /** When this manifest was stored */
  storedAt: number;
  /** Screen ID this manifest belongs to */
  screenId: string;
  /** Whether the TTL has expired */
  get isExpired(): boolean;
}

/** Asset info extracted from a schedule manifest for pre-download */
export interface AssetDownloadInfo {
  assetId: string;
  url: string;
  checksum: string;
  fileSize: number;
  assetType: 'image' | 'video';
  /** Native media duration in seconds (videos; null for images/unknown) */
  duration?: number | null;
  campaignIds: string[];
  campaignEndDates: string[];
  isFallback: boolean;
}

/** Result of an asset download operation */
export interface DownloadResult {
  assetId: string;
  success: boolean;
  storageBackend: 'cache-storage' | 'opfs';
  checksumVerified: boolean;
  error?: string;
}

/** Storage quota info */
export interface StorageQuotaInfo {
  usage: number;
  quota: number;
  available: number;
  headroomTarget: number; // 20% of quota
  isHeadroomMet: boolean;
}

/** Pre-download stats */
export interface PreDownloadStats {
  totalAssets: number;
  downloaded: number;
  skipped: number;
  failed: number;
  evicted: number;
  bytesDownloaded: number;
}
